import { colorAt } from './common';
import { money } from '../lib/format';

/** Donut chart. data: [{label, value, color?}] */
export function Donut({ data = [], size = 150, currency = '₹' }) {
  const items = data.filter((d) => Number(d.value) > 0);
  const total = items.reduce((a, d) => a + Number(d.value), 0);
  const stroke = 22;
  const r = size / 2 - stroke / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  if (total <= 0) {
    return <div className="center" style={{ height: size }}>No data</div>;
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        {items.map((d, i) => {
          const len = (Number(d.value) / total) * c;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={d.color || colorAt(i)}
              strokeWidth={stroke}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return el;
        })}
      </g>
      <text x="50%" y="47%" textAnchor="middle" fontSize="11" fill="var(--muted)">Total</text>
      <text x="50%" y="60%" textAnchor="middle" fontSize="15" fontWeight="700" fill="var(--ink)">
        {money(total, currency)}
      </text>
    </svg>
  );
}

export function Legend({ data = [] }) {
  return (
    <div className="legend">
      {data.filter((d) => Number(d.value) > 0).map((d, i) => (
        <span className="k" key={i}>
          <span className="sw" style={{ background: d.color || colorAt(i) }} />
          {d.label}
        </span>
      ))}
    </div>
  );
}

/** Grouped bars for income vs expense. series: [{label, income, expense}] */
export function IncomeExpenseBars({ series = [], currency = '₹', height = 160 }) {
  const max = Math.max(1, ...series.flatMap((s) => [Number(s.income) || 0, Number(s.expense) || 0]));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height, overflowX: 'auto', paddingTop: 8 }}>
        {series.map((s, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 34 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: height - 22 }}>
              <div title={money(s.income, currency)} style={{ width: 12, height: `${((Number(s.income) || 0) / max) * 100}%`, background: 'var(--income)', borderRadius: '3px 3px 0 0' }} />
              <div title={money(s.expense, currency)} style={{ width: 12, height: `${((Number(s.expense) || 0) / max) * 100}%`, background: 'var(--expense)', borderRadius: '3px 3px 0 0' }} />
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div className="legend">
        <span className="k"><span className="sw" style={{ background: 'var(--income)' }} />Income</span>
        <span className="k"><span className="sw" style={{ background: 'var(--expense)' }} />Expense</span>
      </div>
    </div>
  );
}

/** Simple line/area for a single series. points: [{label, value}] */
export function TrendLine({ points = [], height = 140, color = 'var(--profit)' }) {
  const w = Math.max(280, points.length * 34);
  const vals = points.map((p) => Number(p.value) || 0);
  const max = Math.max(1, ...vals);
  const min = Math.min(0, ...vals);
  const span = max - min || 1;
  const x = (i) => (points.length === 1 ? w / 2 : (i / (points.length - 1)) * (w - 24) + 12);
  const y = (v) => height - 20 - ((v - min) / span) * (height - 32);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(Number(p.value) || 0)}`).join(' ');
  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={w} height={height}>
        <path d={d} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(Number(p.value) || 0)} r="3" fill={color} />
            <text x={x(i)} y={height - 4} textAnchor="middle" fontSize="10" fill="var(--muted)">{p.label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}
